/* eslint-disable react/jsx-curly-newline */
import React, { useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Use History
import { useLocation } from 'react-router-dom';

// Material
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';

// Actions
import { resumeItineraryRequest } from 'store/ducks/itineraries/actions';
import { setStep, setTicketDataRequest } from 'store/ducks/publicStore/actions';
import { listStopsFullRequest } from 'store/ducks/stops/actions';

// Constants
import ENVIRONMENTS from 'constants/enviroments';
import WEEK_DAYS from 'constants/weekDay';
import MONTHS from 'constants/months';

// Hooks
import { useTransport } from '../../../../strategies/transport';
import { useSeatSelection } from '../../../../hooks/useSeatSelection';
import {
  ItineraryListWithCapacity,
  useTripFilter,
} from '../../../../hooks/useTripFilter';
import { ResumeItineraryVesselsInterface } from 'store/ducks/itineraries/types';
import { calcReschedulePriceDifference } from 'helpers/ticketSales';

// Sub-components
import DayPicker from './components/DayPicker';
import TripCard from './components/TripCard';

// Icons
import { Arrow2Down } from 'icons/Arrows';
import {
  BedSeat,
  ConventionalSeat,
  ExecutiveSeat,
} from 'icons/Location_Transport';

// Types
import { ApplicationState } from 'store';
import { ItineraryListInterface } from '../..';

// Styles
import useStyles from './styles';

export const SEAT_TYPE = {
  0: {
    ICON: <ConventionalSeat fill='#000' width='1.5rem' height='1.5rem' />,
    LABEL: 'Convencional',
  },
  1: {
    ICON: <ExecutiveSeat fill='#000' width='1.5rem' height='1.5rem' />,
    LABEL: 'Executiva',
  },
  2: {
    ICON: <BedSeat fill='#000' width='1.5rem' height='1.5rem' />,
    LABEL: 'Leito-cama',
  },
  3: {
    ICON: <BedSeat fill='#000' width='1.5rem' height='1.5rem' />,
    LABEL: 'Cama',
  },
  4: {
    ICON: <BedSeat fill='#000' width='1.5rem' height='1.5rem' />,
    LABEL: 'Cama-Dupla',
  },
};

interface SelectTripProps {
  setIsEmptyItineraryList: (param: boolean) => void;
  setFirstOpen: (param: boolean) => void;
  firstOpen: boolean;
}

type Props = SelectTripProps;

const groupTripsBySeatType = (
  trips: (ItineraryListInterface | ItineraryListWithCapacity)[],
): (ItineraryListInterface | ItineraryListWithCapacity)[] =>
  trips.flatMap((trip) => {
    if (!trip.seats?.length) return [trip];

    const seatsByType = trip.seats.reduce(
      (acc, seat) => ({
        ...acc,
        [seat.seatType]: [...(acc[seat.seatType] || []), seat],
      }),
      {} as Record<number, typeof trip.seats>,
    );

    return Object.entries(seatsByType).map(([currentType, _]) => ({
      ...trip,
      seats: trip.seats.map((seat) => ({
        ...seat,
        enabled: seat.seatType === Number(currentType) && seat.enabled,
      })),
    }));
  });

const SelectTrip: React.FunctionComponent<Props> = ({
  setIsEmptyItineraryList,
  setFirstOpen,
  firstOpen,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const transport = useTransport();
  const XL = window.innerWidth > 1920;
  const MD = window.innerWidth > 1080 && window.innerWidth <= 1320;
  const LG = window.innerWidth > 1280 && window.innerWidth <= 1760;
  const SM = window.innerWidth <= 780;

  const [listControlDays, setListControlDays] = useState<
    { dateTime: number; selected: boolean; title: string }[]
  >([]);
  const [collapse, setCollapse] = useState<string[]>([]);
  const [initialStopId, setInitialStopId] = useState<string>('');
  const [finalStopId, setFinalStopId] = useState<string>('');

  const step = useSelector((state: ApplicationState) => state.publicStore.step);
  const rescheduleData = useSelector(
    (state: ApplicationState) => state.publicStore.rescheduleData,
  );
  const ticketData = useSelector(
    (state: ApplicationState) => state.publicStore.ticketData,
  );
  const isCompleted = useSelector(
    (state: ApplicationState) => state.itineraries.isCompleted,
  );
  const initialItineraryResumeList = useSelector(
    (state: ApplicationState) =>
      state.itineraries.itineraryResumeList as ResumeItineraryVesselsInterface,
  );
  const stopsListFull = useSelector(
    (state: any) => state.stops.stopsListFull || [],
  );

  const {
    itineraryList,
    timeExit,
    setTimeExit,
    vacancies,
    setVacancies,
    orderVessel,
    setOrderVessel,
  } = useTripFilter();

  const getTripSeatType = (
    trip: ItineraryListInterface | ItineraryListWithCapacity,
  ) => (trip as any).seats?.find((seat: any) => seat.enabled)?.seatType;

  const groupedItineraryList = groupTripsBySeatType(itineraryList).filter(
    (trip) => {
      if (!step.confirmRescheduling.toShow) return true;
      return (
        calcReschedulePriceDifference(
          trip as any,
          rescheduleData.amount || 0,
        ) >= 0
      );
    },
  );

  const {
    seatField,
    setSeatField,
    ocupedSeat,
    floor,
    setFloor,
    handleChekSeat,
    getCollapseKey,
  } = useSeatSelection(initialItineraryResumeList);

  const handDleSetDays = (date: Date | null) => {
    if (date) {
      const weekDay =
        WEEK_DAYS.find((item) => item.id === date.getDay())?.shortName || '';
      const dateDay = date.getDate();
      const month =
        MONTHS.find((item) => item.id === date.getMonth() + 1)?.name || '';

      const arrDays = [
        {
          dateTime: date.setHours(0, 0, 0, 0),
          selected: true,
          title: `${weekDay}. ${dateDay} ${month}`,
        },
      ];

      const today = new Date().setHours(0, 0, 0, 0);
      for (let i = 0; i < 4; i += 1) {
        const lastDayInlistControl = arrDays[arrDays.length - 3];
        if (arrDays[0].dateTime > today && !lastDayInlistControl) {
          const firstDay = arrDays[0];
          const previousDay = new Date(
            new Date(firstDay.dateTime).setDate(
              new Date(firstDay.dateTime).getDate() - 1,
            ),
          );
          const wDay =
            WEEK_DAYS.find((item) => item.id === previousDay.getDay())
              ?.shortName || '';
          const dt = previousDay.setHours(0, 0, 0, 0);
          const mo =
            MONTHS.find((item) => item.id === previousDay.getMonth() + 1)
              ?.name || '';
          const pdd = new Date(dt).getDate();
          arrDays.unshift({
            dateTime: dt,
            selected: false,
            title: `${wDay}. ${pdd} ${mo}`,
          });
        } else {
          const lastRightDay = arrDays[arrDays.length - 1];
          const nextDay = new Date(
            new Date(lastRightDay.dateTime).setDate(
              new Date(lastRightDay.dateTime).getDate() + 1,
            ),
          );
          const wDay =
            WEEK_DAYS.find((item) => item.id === nextDay.getDay())?.shortName ||
            '';
          const dt = nextDay.setHours(0, 0, 0, 0);
          const mo =
            MONTHS.find((item) => item.id === nextDay.getMonth() + 1)?.name ||
            '';
          const ndd = new Date(dt).getDate();
          arrDays.push({
            dateTime: dt,
            selected: false,
            title: `${wDay}. ${ndd} ${mo}`,
          });
        }
      }
      setListControlDays(arrDays);
      dispatch(
        setTicketDataRequest({
          ...ticketData,
          date: {
            go: step.go.selected
              ? new Date(date).toISOString()
              : ticketData?.date?.go,
            back: step.back.selected
              ? new Date(date).toISOString()
              : ticketData?.date?.back,
          },
        }),
      );
    }
  };

  const handleNextStep = (
    trip: ItineraryListInterface | ItineraryListWithCapacity,
    holdTokens?: { holdToken: any; tripScheduleSeatId: string }[],
    seatType?: number,
  ) => {
    setSeatField((s: any) => ({ ...s, [trip.tripScheduleId]: [] }));

    if (step.go.selected) {
      if (step.back.toShow) {
        setCollapse([]);
        dispatch(
          setStep({
            go: { ...step.go, selected: false, completed: true },
            back: { ...step.back, selected: true },
            details: step.details,
            confirmRescheduling: { ...step.confirmRescheduling },
            pay: step.pay,
          }),
        );
        if (ticketData.date) handDleSetDays(new Date(ticketData.date.back!));
      } else {
        dispatch(
          setStep({
            go: { ...step.go, selected: false, completed: true },
            back: step.back,
            details: {
              ...step.details,
              selected: step.confirmRescheduling.toShow ? false : true,
            },
            confirmRescheduling: {
              ...step.confirmRescheduling,
              selected: step.confirmRescheduling.toShow ? true : false,
            },
            pay: step.pay,
          }),
        );
      }
      dispatch(
        setTicketDataRequest({
          ...ticketData,
          seatType,
          selectedTravelGo: { ...trip, holdTokens },
        }),
      );
    } else {
      dispatch(
        setStep({
          go: step.go,
          back: { ...step.back, selected: false, completed: true },
          details: { ...step.details, selected: true },
          confirmRescheduling: { ...step.confirmRescheduling },
          pay: step.pay,
        }),
      );
      dispatch(
        setTicketDataRequest({
          ...ticketData,
          seatType,
          selectedTravelBack: { ...trip, holdTokens },
        }),
      );
    }
  };

  const handleConfirmRescheduleStep = (
    trip: ItineraryListInterface,
    holdTokens?: { holdToken: any; tripScheduleSeatId: string }[],
    seatType?: number,
  ) => {
    dispatch(
      setTicketDataRequest({
        ...ticketData,
        seatType,
        selectedTravelGo: { ...trip, holdTokens },
      }),
    );
    dispatch(
      setStep({
        ...step,
        go: { ...step.go, selected: false, completed: true },
        confirmRescheduling: { ...step.confirmRescheduling, selected: true },
      }),
    );
  };

  useEffect(() => {
    if (ticketData?.selectedDay) {
      handDleSetDays(new Date(ticketData.selectedDay));
    } else {
      handDleSetDays(new Date());
    }
  }, []);

  useEffect(() => {
    const found = listControlDays.find((day) => day.selected);
    if (found?.dateTime) {
      let stopId: string | undefined = step.go.selected
        ? ticketData?.initialStop?.id
        : ticketData?.finalStop?.id;
      let endId: string | undefined = step.go.selected
        ? ticketData?.finalStop?.id
        : ticketData?.initialStop?.id;
      if (
        ticketData?.selectedTravelGo?.seats &&
        ticketData?.selectedTravelGo?.seats?.length > 0
      ) {
        stopId = ticketData?.selectedTravelGo?.startStop?.id;
        endId = ticketData?.selectedTravelGo?.endStop?.id;
      }
      if (step.confirmRescheduling.toShow) {
        stopId = rescheduleData.startStopId;
        endId = rescheduleData.endStopId;
      }
      const queryParams = new URLSearchParams(location.search);
      const type = queryParams.get('type');
      const transportOperatorId = queryParams.get('transportOperatorId');
      dispatch(
        resumeItineraryRequest({
          showLoading: true,
          isSecondOpem: true,
          body: {
            itineraryType: type ?? ENVIRONMENTS.TICKET_SALES.ITINERARY_TYPE,
            date: new Date(found.dateTime).toISOString(),
            stopId,
            endId,
            transportOperatorId:
              transportOperatorId ?? ENVIRONMENTS.OPERATOR_ID,
          },
        }),
      );
    }
  }, [listControlDays, location.search, ticketData, step, rescheduleData]);

  useEffect(() => {
    setIsEmptyItineraryList(groupedItineraryList.length === 0);
  }, [groupedItineraryList, setIsEmptyItineraryList]);

  useEffect(() => {
    const hasSeats = groupedItineraryList.some(
      (trip) => trip?.seats?.length > 0,
    );
    if (!hasSeats) return;
    if (ticketData?.selectedTravelGo && !step.back.selected && !firstOpen) {
      setFirstOpen(true);
      const selectedSeatType =
        ticketData.seatType > 0 ? ticketData.seatType : undefined;
      const selectedTrip = groupedItineraryList.find(
        (trip) =>
          trip.tripScheduleId ===
            ticketData?.selectedTravelGo?.tripScheduleId &&
          (!selectedSeatType || getTripSeatType(trip) === selectedSeatType),
      );
      if (selectedTrip) {
        const seatType = selectedSeatType || getTripSeatType(selectedTrip);
        setCollapse([
          getCollapseKey(
            ticketData?.selectedTravelGo?.tripScheduleId,
            seatType,
          ),
        ]);
      }
    }
  }, [
    firstOpen,
    groupedItineraryList,
    ticketData?.selectedTravelGo?.tripScheduleId,
    step,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const initialId = queryParams.get('initialStopId');
    const finalId = queryParams.get('finalStopId');
    if (!initialId || !finalId) return;
    setInitialStopId(initialId);
    setFinalStopId(finalId);
  }, [location.search]);

  useEffect(() => {
    if (initialStopId && finalStopId) {
      dispatch(
        listStopsFullRequest({
          body: { _limit: 9999 },
          transportOperatorId: ENVIRONMENTS.OPERATOR_ID,
        }),
      );
    }
  }, [initialStopId, finalStopId]);

  useEffect(() => {
    if (initialStopId && finalStopId && stopsListFull.length > 0) {
      const initialStop = stopsListFull.find(
        (stop: any) => stop.id === initialStopId,
      );
      const finalStop = stopsListFull.find(
        (stop: any) => stop.id === finalStopId,
      );
      if (initialStop && finalStop) {
        dispatch(
          setStep({
            ...step,
            go: { ...step.go, selected: true, completed: false, toShow: true },
            details: {
              ...step.details,
              selected: false,
              completed: false,
              toShow: true,
            },
            pay: { ...step.pay, toShow: true },
          }),
        );
        dispatch(
          setTicketDataRequest({
            ...ticketData,
            isGoAndBack: true,
            date: { go: new Date().toISOString(), back: null },
            initialStop,
            finalStop,
            selectedDay: new Date().toISOString(),
          }),
        );
      }
    }
  }, [stopsListFull, initialStopId, finalStopId]);

  return (
    <Box className={classes.root}>
      <Box className={classes.filterContainer}>
        <Box mb='0.5rem'>
          <Box pb='0.75rem'>
            <Typography style={{ fontSize: '1.25rem' }}>
              Hora da saída
            </Typography>
          </Box>
          <Box pt='0.25rem' display='flex' alignItems='center'>
            <Checkbox
              className={classes.checkbox}
              onChange={(e) =>
                setTimeExit({ ...timeExit, morning: e.target.checked })
              }
            />
            <Typography className={classes.subTtitle}>
              Manhã • 06:00 - 11:59
            </Typography>
          </Box>
          <Box pt='0.25rem' display='flex' alignItems='center'>
            <Checkbox
              className={classes.checkbox}
              onChange={(e) =>
                setTimeExit({ ...timeExit, afternoon: e.target.checked })
              }
            />
            <Typography className={classes.subTtitle}>
              Tarde • 12:00 - 17:59
            </Typography>
          </Box>
          <Box pt='0.25rem' display='flex' alignItems='center'>
            <Checkbox
              className={classes.checkbox}
              onChange={(e) =>
                setTimeExit({ ...timeExit, night: e.target.checked })
              }
            />
            <Typography className={classes.subTtitle}>
              Noite • 18:00 - 23:59
            </Typography>
          </Box>
        </Box>
        {!step.confirmRescheduling.toShow && (
          <Box>
            <Box pb='0.75rem'>
              <Typography style={{ fontSize: '1.25rem' }}>Vagas</Typography>
            </Box>
            <Box pt='0.25rem' display='flex' alignItems='center'>
              <Checkbox
                className={classes.checkbox}
                onChange={(e) =>
                  setVacancies({ ...vacancies, vehicle: e.target.checked })
                }
              />
              <Typography className={classes.subTtitle}>Veículos</Typography>
            </Box>
            <Box pt='0.25rem' display='flex' alignItems='center'>
              <Checkbox
                className={classes.checkbox}
                onChange={(e) =>
                  setVacancies({ ...vacancies, passagers: e.target.checked })
                }
              />
              <Typography className={classes.subTtitle}>Passageiros</Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box className={classes.tripsContainer}>
        <DayPicker
          listControlDays={listControlDays}
          onDaySelect={handDleSetDays}
          classes={classes}
        />

        {groupedItineraryList.length ? (
          <Box className={classes.travelContainer}>
            <Grid
              style={{ margin: '0 1rem' }}
              alignItems='center'
              justifyContent='space-between'
              container
              xl={12}
              lg={12}
              md={12}
              sm={12}
              xs={12}>
              <Grid xl={4} lg={4} md={4} sm={4} xs={12}>
                <Typography>
                  {groupedItineraryList.length} resultados
                </Typography>
              </Grid>
              <Grid
                xl={8}
                lg={8}
                md={8}
                sm={8}
                xs={12}
                justifyContent='flex-end'
                container>
                {transport.orderOptions.length > 0 && (
                  <Box display='flex' alignItems='center'>
                    <Typography>Ordenar por:</Typography>
                    <TextField
                      className={classes.customMenuItem}
                      select
                      variant='outlined'
                      value={orderVessel}
                      onChange={(e) => setOrderVessel(e.target.value)}
                      SelectProps={{
                        IconComponent: () => (
                          <Arrow2Down width={14} height={14} />
                        ),
                      }}>
                      {transport.orderOptions.map(({ KEY, VALUE }) => (
                        <MenuItem key={KEY} value={KEY}>
                          {VALUE}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}
              </Grid>
            </Grid>
            {isCompleted &&
              groupedItineraryList.map((trip, index) => {
                const collapseKey = getCollapseKey(
                  trip.tripScheduleId,
                  getTripSeatType(trip),
                );
                return (
                  <TripCard
                    key={`${trip.tripScheduleId}-${trip.seats?.filter(
                      (e) => e.enabled,
                    )[0]?.seatType ?? 'default'}-${index}`}
                    trip={trip as any}
                    transport={transport}
                    selectionKey={collapseKey}
                    isCollapsed={collapse.includes(collapseKey)}
                    onToggleCollapse={() => {
                      setCollapse((s) =>
                        s.includes(collapseKey)
                          ? s.filter((id) => id !== collapseKey)
                          : [...s, collapseKey],
                      );
                    }}
                    onSelect={(t) => {
                      if (!trip.seats || trip.seats?.length === 0) {
                        handleNextStep(trip);
                      } else {
                        if (!collapse.includes(collapseKey)) {
                          setCollapse([collapseKey]);
                        } else {
                          setCollapse([]);
                          setSeatField((s: any) => ({
                            ...s,
                            [collapseKey]: [],
                          }));
                          handleChekSeat(
                            t,
                            collapse,
                            transport.isSea,
                            handleNextStep,
                            handleConfirmRescheduleStep,
                          );
                        }
                      }
                    }}
                    seatField={seatField}
                    setSeatField={setSeatField}
                    ocupedSeat={ocupedSeat}
                    floor={floor}
                    setFloor={setFloor}
                    XL={XL}
                    SM={SM}
                    MD={MD}
                    LG={LG}
                    ticketData={ticketData}
                    classes={classes}
                  />
                );
              })}
          </Box>
        ) : (
          isCompleted &&
          groupedItineraryList?.length === 0 && (
            <Box
              pl='2.5rem'
              pt='1rem'
              maxWidth='24rem'
              position='relative'
              zIndex={2}>
              <Typography>0 resultados</Typography>
              <Box pt='5rem'>
                <Typography style={{ fontSize: '2rem', fontWeight: 500 }}>
                  Desculpe
                </Typography>
                <Typography>
                  {step.confirmRescheduling.toShow
                    ? `Não há passagens disponíveis para reagendamento na data selecionada. Por favor, selecione outra data e tente novamente.`
                    : `As vendas para o dia selecionado ainda não foram abertas. Tente novamente mais tarde ou procure por passagens em outros dias.`}
                </Typography>
              </Box>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};
export { SelectTrip };
export default SelectTrip;
